/**
 * Empty State Component
 *
 * The single placeholder used by every list page when its list renders no rows
 * (`/library` guides/patterns/assessments/topics, `/experiments`, `/search`,
 * `/notes`, and the three `/discovery` tabs). It replaced five separate class
 * vocabularies — `guides-empty`, `patterns-empty`, `practice-empty-state`,
 * `session-cards-empty`, `partner-notes__empty`, and discovery's
 * `progress|paths|questions-empty` — which had drifted apart in padding,
 * colour, layout, and wording.
 *
 * Two rules keep it consistent, and both live at the CALL SITE:
 *
 *   1. Render it INSIDE the list container, as a sibling of the
 *      `data-filter-bar-mount` div — never in place of the whole page body.
 *      Replacing the body takes the filter bar and its count down with it,
 *      which is the bug this component was introduced to fix.
 *   2. Test the array you actually RENDER (the filtered one), not the array you
 *      loaded. Testing the source array is how `/library?tab=guides` ended up
 *      showing a blank container to every user with no generated guides.
 *
 * Copy standard: title is sentence case with no terminal period ("No guides
 * yet"); description is one or two complete sentences. When a filter — rather
 * than an absence of data — is what emptied the list, say so: "No guides match
 * these filters" / "Try changing or resetting your filters."
 *
 * @example
 * import { renderEmptyState } from '../components/emptyState.js';
 *
 * container.innerHTML = rows.length
 *   ? rows.map(r => this.renderCard(r)).join('')
 *   : renderEmptyState({
 *       title: 'No guides yet',
 *       description: 'Guides are personalized resources created from your discovery insights.'
 *     });
 */
import { escapeHtml } from '../core/utils.js';

/**
 * Valid `action.dataAction` tokens: the value lands in an HTML attribute AND is
 * dispatched by section click handlers, so it is restricted to an identifier
 * shape rather than merely escaped. A caller cannot smuggle a second attribute
 * (or an inline handler) through this field.
 */
const SAFE_ACTION_TOKEN = /^[a-z][a-z0-9-]*$/;

/**
 * Render the shared list-page empty state.
 *
 * Escapes its own text — callers must NOT pre-escape, or interpolated values
 * (an error message, a partner's name) render double-escaped.
 *
 * @param {Object} opts
 * @param {string} opts.title - Sentence case, no terminal period.
 * @param {string} [opts.description] - One or two complete sentences. Omitted from the markup when absent.
 * @param {boolean} [opts.filtered=false] - Render the "Reset filters" action. Pass this when an ACTIVE FILTER is what emptied the list (not merely an absence of data), and the section handles `[data-action="clear-filters"]` by resetting its filter bar.
 * @param {{label: string, dataAction: string}} [opts.action] - A call-site action
 *   button (e.g. "Start a conversation"). The section handles the emitted
 *   `data-action` in its own click dispatch. `filtered` WINS if both are passed —
 *   a filter hiding the rows is a more urgent thing to resolve than a
 *   suggestion, and the two never co-occur at any current call site.
 * @returns {string} HTML string
 */
export function renderEmptyState({ title, description, filtered = false, action } = {}) {
  const descriptionHtml = description
    ? `<p class="empty-state__description">${escapeHtml(description)}</p>`
    : '';

  let actionHtml = '';
  if (filtered) {
    actionHtml = `<button type="button" class="btn btn--secondary empty-state__action" data-action="clear-filters">Reset filters</button>`;
  } else if (action?.label && SAFE_ACTION_TOKEN.test(action.dataAction || '')) {
    actionHtml = `<button type="button" class="btn btn--secondary empty-state__action" data-action="${action.dataAction}">${escapeHtml(action.label)}</button>`;
  }

  return `
    <div class="empty-state">
      <p class="empty-state__title">${escapeHtml(title)}</p>
      ${descriptionHtml}
      ${actionHtml}
    </div>
  `;
}
